import { UserInvoiceDetailSerializer } from "@app-contexts/user/features/my-orders/data/types/invoice";
import {
  PublicInternalAnalyticsTrackingSerializer,
  PublicInternalAnalyticsTrackingSerializerWithLabelString,
} from "@shared/modules/analytics/types";
import { TicketBasketType } from "@shared/modules/basket/types";
import { isEmpty } from "lodash";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { IAnalyticsClient } from "./Clients/IAnalyticsClient";
import { ShowpassAnalyticsClient } from "./Clients/ShowpassAnalyticsClient";
import {
  AddToCartDataBuilder,
  type AddToCartDataBuilderData,
} from "./DataBuilders/AddToCartDataBuilder";
import { ButtonClickDataBuilder } from "./DataBuilders/ButtonClickDataBuilder";
import { ImpressionDataBuilder } from "./DataBuilders/ImpressionDataBuilder";
import { IInteractionDataBuilder } from "./DataBuilders/IInteractionDataBuilder";
import {
  InitiateCheckoutDataBuilder,
  InitiateCheckoutDataBuilderData,
} from "./DataBuilders/InitiateCheckoutDataBuilder";
import { PageViewDataBuilder } from "./DataBuilders/PageViewDataBuilder";
import { PurchaseCartDataBuilder } from "./DataBuilders/PurchaseCartDataBuilder";
import { SearchDataBuilder } from "./DataBuilders/SearchDataBuilder";
import { RefundProtectionExposureDataBuilder } from "./DataBuilders/RefundProtectionExposureDataBuilder";
import { RefundProtectionSelectionDataBuilder } from "./DataBuilders/RefundProtectionSelectionDataBuilder";
import { RoktEngagementDataBuilder } from "./DataBuilders/RoktEngagementDataBuilder";
import {
  RoktExposureDataBuilder,
  type RoktAnalyticsData,
} from "./DataBuilders/RoktExposureDataBuilder";
import { WalletExposureDataBuilder } from "./DataBuilders/WalletExposureDataBuilder";
import { WalletSelectionDataBuilder } from "./DataBuilders/WalletSelectionDataBuilder";

/**
 * TODO wrap ALL functions in try/catches please
 *
 * InteractionLogger is responsible for tracking user interactions across the application.
 * It follows a builder pattern where each type of interaction (pageview, button click, etc.)
 * has its own data builder that constructs the appropriate payload.
 *
 * The flow works like this:
 * 1. Components/hooks call methods on InteractionLogger (e.g. pageView, addToCart)
 * 2. InteractionLogger uses the appropriate DataBuilder to construct the event payload
 * 3. The payload is sent to an analytics client (default is ShowpassAnalyticsClient)
 * 4. The client sends the data to our analytics backend
 *
 * Common usage:
 * ```ts
 * // Track a page view
 * await UserInteractionLogger.pageView();
 *
 * // Track adding items to cart
 * await UserInteractionLogger.addToCart({
 *   item: cartItem,
 *   basket: currentBasket
 * });
 * ```
 */
export class InteractionLogger {
  /** The analytics client used to send events */
  private client: IAnalyticsClient;

  /**
   * Creates a new InteractionLogger instance
   * @param client - The analytics client to use. If not provided, defaults to ShowpassAnalyticsClient
   */
  constructor(client: IAnalyticsClient = new ShowpassAnalyticsClient()) {
    this.client = client;
  }

  /**
   * Internal method to log events using a data builder
   * @param builder - The data builder that will construct the event payload
   * @returns Promise that resolves to the API Response or null if no payload was built
   */
  private async log(
    builder: IInteractionDataBuilder
  ): Promise<string | undefined> {
    try {
      const payload = builder.build();
      if (!payload) return undefined;

      const resp = await this.client.send(payload);
      const json = await resp?.json();

      return json?.uuid;
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs a page view event
   * @param data - Optional data to include in the page view event
   * @param routeUrl - Optional URL of the page being viewed
   * @returns Promise that resolves to the API Response or null
   */
  public async pageView(
    data: Omit<
      PublicInternalAnalyticsTrackingSerializer,
      "interaction_type"
    > = {},
    routeUrl?: string
  ) {
    try {
      return this.log(new PageViewDataBuilder(data, routeUrl));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs an add to cart event
   * @param data - Data about the item(s) being added to cart
   * @returns Promise that resolves to the API Response or null
   */
  public async addToCart(
    data: AddToCartDataBuilderData | AddToCartDataBuilderData[]
  ) {
    try {
      return this.log(new AddToCartDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs a button click event
   * @param data - Optional data about the button click
   * @returns Promise that resolves to the API Response or null
   */
  public async buttonClick(
    data?: Omit<
      PublicInternalAnalyticsTrackingSerializerWithLabelString,
      "interaction_type"
    >
  ) {
    try {
      return this.log(new ButtonClickDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs an impression (box view) analytics event
   * @param data - Optional data about what is being viewed
   * @returns Promise that resolves to the API Response or null
   */
  public async impression(
    data?: Omit<
      PublicInternalAnalyticsTrackingSerializerWithLabelString,
      "interaction_type"
    >
  ) {
    try {
      if (!isEmpty(data)) {
        return this.log(new ImpressionDataBuilder(data));
      }
      return undefined;
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs a search event
   * @param data - Optional data about the search being performed
   * @returns Promise that resolves to the API Response or null
   */
  public async search(
    data?: Omit<PublicInternalAnalyticsTrackingSerializer, "interaction_type">
  ) {
    try {
      return this.log(new SearchDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs a purchase cart event
   * @param basket - The basket being purchased
   * @param invoice - Optional invoice details for the purchase
   * @returns Promise that resolves to the API Response or null
   */
  public async purchaseCart(
    basket: TicketBasketType,
    invoice?: UserInvoiceDetailSerializer,
    extraCustomParams?: Record<string, unknown>
  ) {
    try {
      if (!basket.item_groups) return undefined;
      return this.log(
        new PurchaseCartDataBuilder(
          basket,
          invoice ?? undefined,
          extraCustomParams
        )
      );
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  /**
   * Logs an initiate checkout event which is NOT ONLY used for tracking when checkout was initiated,
   * but rather used for any event during the checkout process........
   * Yup
   *
   * @param data - Data about the checkout being initiated
   * @returns Promise that resolves to the API Response or null
   */
  public async initiateCheckout(data: InitiateCheckoutDataBuilderData) {
    try {
      return this.log(new InitiateCheckoutDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }

    return undefined;
  }

  public async refundProtectionExposure(data: InitiateCheckoutDataBuilderData) {
    try {
      return this.log(new RefundProtectionExposureDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }
    return undefined;
  }

  public async refundProtectionSelection(
    data: InitiateCheckoutDataBuilderData
  ) {
    try {
      return this.log(new RefundProtectionSelectionDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }
    return undefined;
  }

  public async walletExposure(data: InitiateCheckoutDataBuilderData) {
    try {
      return this.log(new WalletExposureDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }
    return undefined;
  }

  public async walletSelection(
    data: InitiateCheckoutDataBuilderData,
    blocked = false
  ) {
    try {
      return this.log(
        new WalletSelectionDataBuilder({
          ...data,
          additionalParams: {
            ...data.additionalParams,
            is_click_blocked: blocked,
          },
        })
      );
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }
    return undefined;
  }

  public async roktExposure(data: RoktAnalyticsData) {
    try {
      return this.log(new RoktExposureDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }
    return undefined;
  }

  public async roktEngagement(data: RoktAnalyticsData) {
    try {
      return this.log(new RoktEngagementDataBuilder(data));
    } catch (e) {
      logAppInfo("Failed to track interaction");
      recordAppError(e);
    }
    return undefined;
  }
}

// Export a singleton instance of the logger that uses the default ShowpassAnalyticsClient
const UserInteractionLogger = new InteractionLogger();
export default UserInteractionLogger;
